import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  createCart, 
  addToCart as addToCartAPI, 
  updateCartLines, 
  removeFromCart as removeFromCartAPI,
  getCart 
} from '../services/shopifyClient';

const CartContext = createContext(null);

const CART_ID_KEY = 'shopify_cart_id';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Initialize cart from localStorage
  useEffect(() => {
    const initializeCart = async () => {
      const savedCartId = localStorage.getItem(CART_ID_KEY);
      if (savedCartId) {
        const existingCart = await getCart(savedCartId);
        if (existingCart) {
          setCart(existingCart);
        } else {
          // Cart no longer exists, clear storage
          localStorage.removeItem(CART_ID_KEY);
        }
      }
    };
    initializeCart();
  }, []);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Add item to cart
  const addToCart = useCallback(async (variantId, quantity = 1) => {
    setIsLoading(true);
    try {
      let updatedCart;
      
      if (!cart) {
        // Create new cart with item
        updatedCart = await createCart([{ merchandiseId: variantId, quantity }]);
        if (updatedCart) {
          localStorage.setItem(CART_ID_KEY, updatedCart.id);
        }
      } else {
        // Add to existing cart
        updatedCart = await addToCartAPI(cart.id, [{ merchandiseId: variantId, quantity }]);
      }

      if (updatedCart) {
        setCart(updatedCart);
        showNotification('Added to cart!');
        setIsCartOpen(true);
      } else {
        showNotification('Failed to add to cart', 'error');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showNotification('Failed to add to cart', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [cart]);

  // Update item quantity
  const updateQuantity = useCallback(async (lineId, quantity) => {
    if (!cart) return;
    
    setIsLoading(true);
    try {
      const updatedCart = await updateCartLines(cart.id, [{ id: lineId, quantity }]);
      if (updatedCart) {
        setCart(updatedCart);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      showNotification('Failed to update quantity', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [cart]);

  // Remove item from cart
  const removeFromCart = useCallback(async (lineId) => {
    if (!cart) return;
    
    setIsLoading(true);
    try {
      const updatedCart = await removeFromCartAPI(cart.id, [lineId]);
      if (updatedCart) {
        setCart(updatedCart);
        showNotification('Item removed');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      showNotification('Failed to remove item', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [cart]);

  // Clear cart
  const clearCart = useCallback(() => {
    localStorage.removeItem(CART_ID_KEY);
    setCart(null);
  }, []);

  // Get cart items formatted
  const cartItems = cart?.lines?.edges?.map(edge => ({
    id: edge.node.id,
    quantity: edge.node.quantity,
    variantId: edge.node.merchandise.id,
    variantTitle: edge.node.merchandise.title,
    productTitle: edge.node.merchandise.product.title,
    price: parseFloat(edge.node.merchandise.price.amount),
    currency: edge.node.merchandise.price.currencyCode,
    image: edge.node.merchandise.product.images?.edges?.[0]?.node?.url,
    imageAlt: edge.node.merchandise.product.images?.edges?.[0]?.node?.altText,
  })) || [];

  const totalQuantity = cart?.totalQuantity || 0;
  const subtotal = cart?.cost?.subtotalAmount?.amount 
    ? parseFloat(cart.cost.subtotalAmount.amount).toFixed(2) 
    : '0.00';
  const total = cart?.cost?.totalAmount?.amount 
    ? parseFloat(cart.cost.totalAmount.amount).toFixed(2) 
    : '0.00';
  const checkoutUrl = cart?.checkoutUrl || null;

  const value = {
    cart,
    cartItems,
    totalQuantity,
    subtotal,
    total,
    checkoutUrl,
    isCartOpen,
    setIsCartOpen,
    isLoading,
    notification,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
